import React, { useState, useEffect } from 'react';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFilters?: any;
  specificResults?: any[];
}

interface FieldCategory {
  [key: string]: string;
}

interface FieldsInfo {
  core_identifiers: FieldCategory;
  exosite_data: FieldCategory;
  binding_data: FieldCategory;
  experimental_data: FieldCategory;
  publication_data: FieldCategory;
  quality_control: FieldCategory;
  computed_fields: FieldCategory;
}

const XMarkIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const DownloadIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V9a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, currentFilters = {}, specificResults = [] }) => {
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [isExporting, setIsExporting] = useState(false);

  const getExportFields = () => {
    return ''; // Export all fields
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const backendUrl = '';

      // If specific results are provided, use client-side export
      if (specificResults && specificResults.length > 0) {
        exportSpecificResults();
        return;
      }

      // Build export URL with parameters for server-side export
      const params = new URLSearchParams();
      
      // Always include parsed residues and quality scores
      params.append('include_parsed_residues', 'true');
      params.append('include_quality_scores', 'true');

      // Add format parameter to URL
      params.append('format', exportFormat);
      
      const exportUrl = `${backendUrl}/export?${params.toString()}`;
      
      if (exportFormat === 'json') {
        // For JSON, fetch the data and show integrity information
        const response = await fetch(exportUrl);
        const data = await response.json();
        
        // Show integrity information to user
        const confirmDownload = window.confirm(
          `JSON Export Ready!\n\n` +
          `Schema Version: ${data.schema_version}\n` +
          `Entries: ${data.metadata.total_entries}\n` +
          `Checksum: ${data.data_checksum.substring(0, 16)}...\n\n` +
          `Click OK to download the verified JSON file.`
        );
        
        if (confirmDownload) {
          // Create and download the JSON file
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const link = document.createElement('a');
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', `exositedb_export_${data.export_timestamp.replace(/[:.]/g, '-')}.json`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        // For CSV, use direct download
        const link = document.createElement('a');
        link.href = exportUrl;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Close modal after successful export
      setTimeout(() => {
        onClose();
      }, 1000);
      
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportSpecificResults = () => {
    try {
      if (exportFormat === 'json') {
        // Convert specific results to JSON format
        const jsonData = convertResultsToJSON(specificResults);
        
        // Create integrity information
        const exportData = {
          schema_version: "2.0.0",
          export_timestamp: new Date().toISOString(),
          data_checksum: calculateClientChecksum(jsonData),
          metadata: {
            total_entries: jsonData.length,
            export_type: "search_results",
            export_filters: currentFilters,
            schema_fields: Object.keys(jsonData[0] || {}),
            fields_included: "all"
          },
          data: jsonData,
          integrity: {
            algorithm: "SHA-256",
            checksum: calculateClientChecksum(jsonData),
            verified: true
          }
        };
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `search_results_${new Date().toISOString().split('T')[0]}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // Convert specific results to CSV format
        const csvContent = convertResultsToCSV(specificResults);
        
        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `search_results_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      // Close modal after successful export
      setTimeout(() => {
        onClose();
      }, 1000);
      
    } catch (error) {
      console.error('Error exporting specific results:', error);
      alert('Export failed. Please try again.');
    }
  };

  const convertResultsToCSV = (results: any[]): string => {
    if (!results || results.length === 0) return '';

    // Define headers from id through pmc_id + family_class (publication data only, no internal fields)
    const headers = [
      'id', 'protein_name', 'pdb_id', 'uniprot_id', 'uniprot_id_inferred', 'uniprot_id_confidence',
      'organism', 'exosite_residues_reported', 'exosite_residues_standard', 'exosite_residues_uniprot',
      'exosite_residues_pdb', 'orthosteric_site_residues', 'experimental_method', 'binding_affinity',
      'functional_classification', 'disease_relevance', 'structural_data', 'binding_partner',
      'binding_partners', 'exosite_summary', 'exosite_type',
      'pubmed_id', 'doi', 'pmc_id', 'family_class'
    ];

    // Create CSV content
    let csvContent = headers.join(',') + '\n';

    results.forEach(entry => {
      const actualEntry = entry.entry_type === 'individual' ? entry.individual_entry :
                         (entry.entry_type === 'grouped' ? entry.grouped_entry?.best_entry : null);
      if (!actualEntry) return;

      const row = headers.map(header => {
        let value = actualEntry[header] || '';
        // Escape commas and quotes in CSV values
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""');
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }
        }
        return value;
      }).join(',');

      csvContent += row + '\n';
    });

    return csvContent;
  };

  const convertResultsToJSON = (results: any[]): any[] => {
    if (!results || results.length === 0) return [];

    // Define fields from id through pmc_id + family_class (publication data only, no internal fields)
    const fields = [
      'id', 'protein_name', 'pdb_id', 'uniprot_id', 'uniprot_id_inferred', 'uniprot_id_confidence',
      'organism', 'exosite_residues_reported', 'exosite_residues_standard', 'exosite_residues_uniprot',
      'exosite_residues_pdb', 'orthosteric_site_residues', 'experimental_method', 'binding_affinity',
      'functional_classification', 'disease_relevance', 'structural_data', 'binding_partner',
      'binding_partners', 'exosite_summary', 'exosite_type',
      'pubmed_id', 'doi', 'pmc_id', 'family_class'
    ];

    return results.map(entry => {
      const actualEntry = entry.entry_type === 'individual' ? entry.individual_entry :
                         (entry.entry_type === 'grouped' ? entry.grouped_entry?.best_entry : null);
      if (!actualEntry) return null;

      const jsonEntry: any = {};
      fields.forEach(field => {
        jsonEntry[field] = actualEntry[field] || null;
      });

      return jsonEntry;
    }).filter(entry => entry !== null);
  };

  const calculateClientChecksum = (data: any): string => {
    // Simple client-side checksum for consistency
    // In a real implementation, you might use a proper crypto library
    const dataString = JSON.stringify(data, Object.keys(data).sort());
    let hash = 0;
    for (let i = 0; i < dataString.length; i++) {
      const char = dataString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16).padStart(8, '0');
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '40px',
        maxWidth: '520px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              padding: '12px',
              backgroundColor: '#eff6ff',
              borderRadius: '12px',
              border: '1px solid #dbeafe'
            }}>
              <DownloadIcon style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
            </div>
            <h2 style={{ margin: 0, fontSize: '32px', fontWeight: 'bold', color: '#1e293b', letterSpacing: '-0.025em' }}>
              Data Export
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '10px',
              color: '#6b7280',
              borderRadius: '10px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <XMarkIcon style={{ width: '24px', height: '24px' }} />
          </button>
        </div>

        {/* Summary */}
        <div style={{ 
          backgroundColor: '#f8fafc', 
          border: '1px solid #e2e8f0',
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '32px' 
        }}>
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
            Export Summary
          </div>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {specificResults.length > 0 ? 
              `${specificResults.length} filtered entries ready for export` :
              'Complete database export with all available fields'
            }
          </div>
        </div>

        {/* Format Selection */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
            Choose Format
          </h3>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setExportFormat('csv')}
              style={{
                flex: 1,
                padding: '16px',
                borderRadius: '10px',
                border: exportFormat === 'csv' ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                backgroundColor: exportFormat === 'csv' ? '#eff6ff' : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '16px', fontWeight: '600', color: exportFormat === 'csv' ? '#3b82f6' : '#374151', marginBottom: '4px' }}>
                CSV
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                Spreadsheet compatible
              </div>
            </button>
            
            <button
              onClick={() => setExportFormat('json')}
              style={{
                flex: 1,
                padding: '16px',
                borderRadius: '10px',
                border: exportFormat === 'json' ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                backgroundColor: exportFormat === 'json' ? '#eff6ff' : 'white',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '16px', fontWeight: '600', color: exportFormat === 'json' ? '#3b82f6' : '#374151', marginBottom: '4px' }}>
                JSON
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                With verification
              </div>
            </button>
          </div>
        </div>


        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '14px 28px',
              backgroundColor: 'white',
              color: '#6b7280',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.color = '#374151';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.color = '#6b7280';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '14px 28px',
              backgroundColor: isExporting ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: isExporting ? 'none' : '0 4px 12px rgba(59, 130, 246, 0.25)',
              letterSpacing: '-0.015em'
            }}
            onMouseEnter={(e) => {
              if (!isExporting) {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.35)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isExporting) {
                e.currentTarget.style.backgroundColor = '#3b82f6';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
              }
            }}
          >
            <DownloadIcon style={{ width: '18px', height: '18px' }} />
{isExporting ? 'Exporting...' : `Export ${exportFormat.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;